import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, formatErr, fmtDate, fmtDateTime } from "../lib/api";
import { Page } from "../components/AppShell";
import {
  Briefcase, FloppyDisk, MapPin, IdentificationCard, FileText, ListBullets,
  CalendarBlank, Receipt, ArrowLeft, CheckCircle, CircleNotch, Trash, Plus, Printer,
  ShieldCheck,
} from "@phosphor-icons/react";

/* --------------------------------------------------------------------------
 * /job-dash/:id
 * Full job editor with auto-binding: every field PATCHes on blur (debounced)
 * with no Save button. A subtle "saved" indicator confirms persistence.
 * -------------------------------------------------------------------------- */

const PATCH_DEBOUNCE_MS = 400;

export default function JobDash() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [saveStatus, setSaveStatus] = useState("idle"); // idle | saving | saved | error
  const [lookups, setLookups] = useState({ statuses: [], types: [], locations: [], users: [] });

  const load = async () => {
    try {
      const r = await api.get(`/jobs/${id}/details`);
      setData(r.data);
    } catch (e) { setErr(formatErr(e)); }
  };

  useEffect(() => {
    let cancelled = false;
    const loadAll = async () => {
      try {
        const [details, s, t, l, m] = await Promise.all([
          api.get(`/jobs/${id}/details`),
          api.get("/jobs/statuses").catch(() => ({ data: [] })),
          api.get("/jobs/types").catch(() => ({ data: [] })),
          api.get("/locations").catch(() => ({ data: [] })),
          api.get("/admin/members").catch(() => ({ data: [] })),
        ]);
        if (cancelled) return;
        setData(details.data);
        setLookups({
          statuses: s.data || [],
          types: t.data || [],
          locations: l.data || [],
          users: m.data || [],
        });
        // iter 39 — Stamp this job into the user's recently-viewed list so it
        // shows up in the TopBar's Recent Jobs popover. Fire-and-forget; a
        // failure here must not block the dash from rendering.
        api.post("/users/me/recent-jobs", { job_id: id }).catch(() => {});
      } catch (e) { if (!cancelled) setErr(formatErr(e)); }
    };
    loadAll();
    return () => { cancelled = true; };
  }, [id]);

  const patchJob = async (patch) => {
    setSaveStatus("saving");
    try {
      const r = await api.patch(`/jobs/${id}`, patch);
      setData((d) => d ? { ...d, job: { ...d.job, ...r.data } } : d);
      setSaveStatus("saved");
      window.setTimeout(() => setSaveStatus((s) => s === "saved" ? "idle" : s), 1200);
    } catch (e) {
      setErr(formatErr(e));
      setSaveStatus("error");
    }
  };

  if (err) return <div className="p-8 text-destructive" data-testid="jobdash-error">{err}</div>;
  if (!data) return (
    <div className="p-12 text-center text-muted-foreground" data-testid="jobdash-loading">
      <CircleNotch size={20} className="animate-spin mx-auto mb-2" /> Loading job…
    </div>
  );

  const j = data.job;

  return (
    <Page
      title={`Job · ${j.job_number}`}
      subtitle="AUTO-SAVE EDIT MODE — CHANGES PERSIST ON BLUR"
      breadcrumbs={[{ label: "Dashboard", to: "/" }, { label: "Jobs", to: "/jobs" }, j.job_number]}
      testid="job-dash-page"
      actions={
        <div className="flex items-center gap-2" data-testid="jobdash-save-status">
          <a
            href={`${process.env.REACT_APP_BACKEND_URL}/api/jobs/${id}/field-sheet.pdf`}
            target="_blank"
            rel="noreferrer"
            className="btn btn-ghost btn-sm"
            data-testid="jobdash-field-sheet-link"
            title="Open Field Sheet PDF (with QR codes)"
          >
            <Printer size={12} /> FIELD SHEET
          </a>
          {saveStatus === "saving" && <span className="text-xs text-muted-foreground flex items-center gap-1"><CircleNotch size={12} className="animate-spin" /> Saving…</span>}
          {saveStatus === "saved" && <span className="text-xs text-green-600 flex items-center gap-1"><CheckCircle size={12} /> Saved</span>}
          {saveStatus === "error" && <span className="text-xs text-destructive flex items-center gap-1"><FloppyDisk size={12} /> Save failed</span>}
        </div>
      }
    >
      <div className="max-w-6xl mx-auto">
      <button
        onClick={() => navigate("/jobs")}
        className="btn btn-ghost btn-sm mb-3"
        data-testid="jobdash-back"
      >
        <ArrowLeft size={12} /> JOBS
      </button>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Section title="JOB" icon={Briefcase} testid="sec-job">
          <AutoSelect label="STATUS" value={j.job_status_id} options={lookups.statuses}
            onSave={(v) => patchJob({ job_status_id: v || null })} testid="field-status" />
          <AutoSelect label="TYPE" value={j.job_type_id} options={lookups.types}
            onSave={(v) => patchJob({ job_type_id: v || null })} testid="field-type" />
          <AutoSelect
            label="ASSIGNED TO" value={j.assigned_user_id}
            options={lookups.users.map((u) => ({ id: u.id, name: u.name || u.email }))}
            onSave={(v) => patchJob({ assigned_user_id: v || null })} testid="field-assignee"
          />
          <AutoInput label="DUE DATE" type="datetime-local" value={toDtLocal(j.due_date)}
            onSave={(v) => patchJob({ due_date: v ? new Date(v).toISOString() : null })} testid="field-due-date" />
          <AutoInput label="SERVED AT" type="datetime-local" value={toDtLocal(j.served_at)}
            onSave={(v) => patchJob({ served_at: v ? new Date(v).toISOString() : null })} testid="field-served-at" />
          <AutoInput label="PRICE" type="number" value={j.price}
            onSave={(v) => patchJob({ price: v ? Number(v) : 0 })} testid="field-price" />
          <AutoTextarea label="NOTES" value={j.notes || ""}
            onSave={(v) => patchJob({ notes: v || null })} testid="field-notes" />
        </Section>

        <Section title="CASE" icon={FileText} testid="sec-case">
          {data.case ? (
            <div className="space-y-1">
              <KV label="CASE NUMBER" v={data.case.case_number} />
              <KV label="PLAINTIFF" v={data.case.plaintiff} />
              <KV label="DEFENDANT" v={data.case.defendant} />
              <Link to={`/cases?highlight=${data.case.id}`} className="text-xs text-primary hover:underline" data-testid="link-case">
                Open case →
              </Link>
            </div>
          ) : (
            <div className="text-xs text-muted-foreground italic">No case linked.</div>
          )}
        </Section>

        <Section title="SUBJECT" icon={IdentificationCard} testid="sec-subject">
          {data.subject ? (
            <div className="space-y-1">
              <KV label="NAME" v={`${data.subject.first_name || ""} ${data.subject.last_name || ""}`.trim() || "—"} />
              <KV label="AKA" v={data.subject.aka} />
              <KV label="DOB" v={data.subject.date_of_birth ? fmtDate(data.subject.date_of_birth) : "—"} />
              <KV label="SSN (last 4)" v={data.subject.ssn_last4 ? `****${data.subject.ssn_last4}` : "—"} />
              <Link to={`/subjects?highlight=${data.subject.id}`} className="text-xs text-primary hover:underline" data-testid="link-subject">
                Open subject →
              </Link>
            </div>
          ) : (
            <div className="text-xs text-muted-foreground italic">No subject attached. Use the + SUBJECT picker on the Jobs page.</div>
          )}
        </Section>

        <Section title="SERVICE LOCATION" icon={MapPin} testid="sec-location">
          <AutoSelect
            label="LOCATION"
            value={j.service_location_id}
            options={lookups.locations.map((l) => ({ id: l.id, name: l.name || l.address1 }))}
            onSave={(v) => patchJob({ service_location_id: v || null })}
            testid="field-location"
          />
          {data.service_location && (
            <div className="text-xs text-muted-foreground mono mt-2">
              {[data.service_location.address1, data.service_location.city, data.service_location.state, data.service_location.postal_code].filter(Boolean).join(", ")}
            </div>
          )}
        </Section>

        <Section title="NOTES" icon={ListBullets} testid="sec-notes" wide>
          <NotesEditor jobId={id} initial={data.notes || []} onChange={load} />
        </Section>

        <Section title="SERVICE ATTEMPTS" icon={CalendarBlank} testid="sec-attempts" wide>
          <div className="text-xs text-muted-foreground italic">
            Service-attempts schema will land in iter 24 (the &quot;document attempts&quot; module). The Job Mini-Panel
            currently shows the stub. Auto-binding event log will populate this section then.
          </div>
        </Section>

        <Section title="GLBA VERIFICATIONS" icon={ShieldCheck} testid="sec-glba" wide>
          <GlbaPanel jobId={id} subjectId={j.subject_id} />
        </Section>

        <Section title="CALENDAR" icon={CalendarBlank} testid="sec-calendar">
          {data.calendar_items.length === 0 ? (
            <div className="text-xs text-muted-foreground italic">No calendar items linked.</div>
          ) : data.calendar_items.map((ci) => (
            <div key={ci.id} className="flex justify-between py-1 border-b border-border last:border-0">
              <span className="text-xs">{ci.type || "—"}</span>
              <span className="text-[10px] mono text-muted-foreground">{fmtDateTime(ci.start_time)}</span>
            </div>
          ))}
        </Section>

        <Section title="INVOICES" icon={Receipt} testid="sec-invoices">
          {data.invoices.length === 0 ? (
            <div className="text-xs text-muted-foreground italic">No invoices.</div>
          ) : data.invoices.slice(0, 5).map((inv) => (
            <div key={inv.id} className="flex justify-between py-1 border-b border-border last:border-0 text-xs">
              <span className="mono">{inv.invoice_number}</span>
              <span className="mono">${inv.balance.toFixed(2)}</span>
            </div>
          ))}
        </Section>
      </div>
      </div>
    </Page>
  );
}

/* -------------------------------------------------------------------------- */
/* Auto-binding primitives                                                    */
/* -------------------------------------------------------------------------- */

function AutoInput({ label, value, onSave, type = "text", testid }) {
  const [local, setLocal] = useState(value ?? "");
  const timer = useRef(null);
  const debouncedSave = (v) => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => onSave(v), PATCH_DEBOUNCE_MS);
  };
  return (
    <div className="py-1">
      <div className="label-xs mb-0.5">{label}</div>
      <input
        className="input"
        type={type}
        value={local}
        onChange={(e) => { setLocal(e.target.value); debouncedSave(e.target.value); }}
        onBlur={() => onSave(local)}
        data-testid={testid}
      />
    </div>
  );
}

function AutoTextarea({ label, value, onSave, testid }) {
  const [local, setLocal] = useState(value ?? "");
  const timer = useRef(null);
  const debouncedSave = (v) => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => onSave(v), PATCH_DEBOUNCE_MS);
  };
  return (
    <div className="py-1">
      <div className="label-xs mb-0.5">{label}</div>
      <textarea
        className="input min-h-[80px]"
        value={local}
        onChange={(e) => { setLocal(e.target.value); debouncedSave(e.target.value); }}
        onBlur={() => onSave(local)}
        data-testid={testid}
      />
    </div>
  );
}

function AutoSelect({ label, value, options, onSave, testid }) {
  return (
    <div className="py-1">
      <div className="label-xs mb-0.5">{label}</div>
      <select
        className="input"
        value={value || ""}
        onChange={(e) => onSave(e.target.value || null)}
        data-testid={testid}
      >
        <option value="">— none —</option>
        {options.map((o) => (
          <option key={o.id} value={o.id}>{o.name}</option>
        ))}
      </select>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Notes editor                                                                */
/* -------------------------------------------------------------------------- */

function NotesEditor({ jobId, initial, onChange }) {
  const [notes, setNotes] = useState(initial);
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);

  const add = async () => {
    const b = body.trim();
    if (!b) return;
    setBusy(true);
    try {
      const r = await api.post(`/jobs/${jobId}/notes`, { body: b });
      setNotes((ns) => [r.data, ...ns]);
      setBody("");
      onChange && onChange();
    } finally { setBusy(false); }
  };

  const del = async (nid) => {
    try {
      await api.delete(`/jobs/${jobId}/notes/${nid}`);
      setNotes((ns) => ns.filter((n) => n.id !== nid));
    } catch { /* ignore */ }
  };

  return (
    <div data-testid="notes-editor">
      <div className="flex gap-2 mb-2">
        <input
          className="input"
          placeholder="Add a note…"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && add()}
          data-testid="notes-input"
        />
        <button className="btn btn-primary" onClick={add} disabled={busy || !body.trim()} data-testid="notes-add">
          <Plus size={12} /> ADD
        </button>
      </div>
      {notes.length === 0 && <div className="text-xs text-muted-foreground italic">No notes yet.</div>}
      {notes.map((n) => (
        <div key={n.id} className="flex items-start justify-between py-2 border-b border-border last:border-0 gap-2" data-testid={`note-${n.id}`}>
          <div className="flex-1">
            <div className="text-xs whitespace-pre-wrap">{n.body}</div>
            <div className="text-[10px] mono text-muted-foreground">{fmtDateTime(n.created_at)}</div>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={() => del(n.id)} data-testid={`note-delete-${n.id}`}>
            <Trash size={12} />
          </button>
        </div>
      ))}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Layout helpers                                                              */
/* -------------------------------------------------------------------------- */

const Section = ({ title, icon: I, children, testid, wide }) => (
  <div className={`surface p-4 ${wide ? "md:col-span-2" : ""}`} data-testid={testid}>
    <div className="flex items-center gap-2 mb-3"><I size={14} /><span className="label-xs">{title}</span></div>
    {children}
  </div>
);

const KV = ({ label, v }) => (
  <div className="flex justify-between text-xs py-0.5 border-b border-border last:border-0">
    <span className="label-xs">{label}</span>
    <span className="text-right text-muted-foreground">{v || "—"}</span>
  </div>
);

function toDtLocal(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}


/* -------------------------------------------------------------------------- */
/* Iter 31 — In-job GLBA verification panel                                   */
/* -------------------------------------------------------------------------- */
function GlbaPanel({ jobId, subjectId }) {
  const [verifs, setVerifs] = useState([]);
  const [certTypes, setCertTypes] = useState([]);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({ certification_type_id: "", purpose: "", response_summary: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get(`/glba-verifications?job_id=${jobId}`).then(r => setVerifs(r.data)).catch(() => {});
  };
  useEffect(() => {
    load();
    api.get("/glba-certification-types").then(r => setCertTypes((r.data || []).filter(t => t.is_active !== false))).catch(() => setCertTypes([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  const save = async () => {
    setErr("");
    if (!draft.certification_type_id && !draft.purpose.trim()) {
      setErr("Pick a certification type or write a permissible purpose.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/glba-verifications", {
        job_id: jobId,
        subject_id: subjectId || null,
        certification_type_id: draft.certification_type_id || null,
        purpose: draft.purpose.trim() || null,
        response_summary: draft.response_summary.trim() || null,
        verified_at: new Date().toISOString(),
      });
      setDraft({ certification_type_id: "", purpose: "", response_summary: "" });
      setAdding(false);
      load();
    } catch (e) { setErr(formatErr(e)); }
    finally { setBusy(false); }
  };

  const remove = async (v) => {
    if (!window.confirm("Delete this verification?")) return;
    try { await api.delete(`/glba-verifications/${v.id}`); load(); }
    catch (e) { setErr(formatErr(e)); }
  };

  return (
    <div className="space-y-3" data-testid="glba-panel">
      {verifs.length === 0 ? (
        <div className="text-xs text-muted-foreground italic" data-testid="glba-empty">
          No verifications logged for this job yet.
        </div>
      ) : (
        <div className="space-y-2">
          {verifs.map(v => (
            <div key={v.id} className="surface p-3 flex justify-between items-start gap-3" data-testid={`glba-row-${v.id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs font-semibold uppercase mono">
                    {v.certification_type_name || certTypes.find(t => t.id === v.certification_type_id)?.name || "—"}
                  </span>
                  <span className="text-[10px] text-muted-foreground mono">
                    {v.verified_at ? fmtDateTime(v.verified_at) : "no timestamp"}
                  </span>
                </div>
                {v.purpose && <div className="text-xs text-muted-foreground">{v.purpose}</div>}
                {v.response_summary && <div className="text-xs text-muted-foreground italic mt-1">{v.response_summary}</div>}
              </div>
              <button className="btn btn-ghost btn-sm" onClick={() => remove(v)} data-testid={`glba-delete-${v.id}`}>
                <Trash size={12} />
              </button>
            </div>
          ))}
        </div>
      )}

      {adding ? (
        <div className="surface p-3 space-y-2" data-testid="glba-add-form">
          <div>
            <label className="label-xs">CERTIFICATION TYPE</label>
            <select
              className="input w-full"
              value={draft.certification_type_id}
              onChange={(e) => setDraft({ ...draft, certification_type_id: e.target.value })}
              data-testid="glba-cert-type-select"
            >
              <option value="">— select a type —</option>
              {certTypes.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
            {certTypes.length === 0 && (
              <div className="text-[10px] text-amber-700 mt-1">
                No certification types yet. <Link to="/templates" className="underline">Set them up in Templates</Link>.
              </div>
            )}
          </div>
          <div>
            <label className="label-xs">PERMISSIBLE PURPOSE (optional, free-form)</label>
            <input
              className="input w-full text-xs"
              value={draft.purpose}
              onChange={(e) => setDraft({ ...draft, purpose: e.target.value })}
              placeholder="Locate purpose under FCRA Sec 604(a)…"
              data-testid="glba-purpose-input"
            />
          </div>
          <div>
            <label className="label-xs">RESPONSE SUMMARY (optional)</label>
            <textarea
              className="input w-full text-xs"
              rows={2}
              value={draft.response_summary}
              onChange={(e) => setDraft({ ...draft, response_summary: e.target.value })}
              data-testid="glba-response-input"
            />
          </div>
          {err && <div className="text-xs text-destructive" data-testid="glba-add-error">{err}</div>}
          <div className="flex gap-2">
            <button className="btn btn-primary btn-sm" onClick={save} disabled={busy} data-testid="glba-save-btn">
              {busy ? "SAVING…" : <><CheckCircle size={12} /> LOG VERIFICATION</>}
            </button>
            <button className="btn btn-ghost btn-sm" onClick={() => { setAdding(false); setErr(""); }} data-testid="glba-cancel-btn">
              CANCEL
            </button>
          </div>
        </div>
      ) : (
        <button className="btn btn-secondary btn-sm" onClick={() => setAdding(true)} data-testid="glba-add-btn">
          <Plus size={12} /> ADD VERIFICATION
        </button>
      )}
    </div>
  );
}
